import { useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion, AccordionTab, Iconify, Button, ButtonVariant, ButtonSize,
} from "@repo/design-system/components/ui";
import EditAcademicRecordModal, { PROGRAM_LEVEL_ID } from "./EditAcademicRecordModal";
import AddAcademicRecordModal from "./AddAcademicRecordModal";

const InfoField = ({ label, value }) => (
  <div className="sp-info-field">
    <span className="sp-info-label">{label}</span>
    <span className="sp-info-value">{value || "-"}</span>
  </div>
);

// Rounds numeric scores to 2 decimals (e.g. 61.3333333 -> "61.33"); non-numeric values pass through
const formatScore = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : value;
};

const SCHOOL_RECORD_TITLES = { ssc: "SSC Academic Records :", hsc: "HSC Academic Records :" };
// The array key an item came from (ug/pg/diploma/phd/other) is the reliable source of its
// level — the backend's own programLevelName on the item can be wrong (e.g. a phd record
// sent with programLevelName "Diploma"), so it's never used for the display label.
const LEVEL_LABELS = { ug: "UG", pg: "PG", diploma: "Diploma", phd: "PhD", other: "Other" };

// A college record whose mutable and immutable objects are both empty (or hold only blank values) has no block.
const hasAnyValue = (item) =>
  Object.values({ ...(item?.immutable ?? {}), ...(item?.mutable ?? {}) })
    .some((value) => value !== undefined && value !== null && String(value).trim() !== "");

const AcademicHistoryInfo = ({ academicHistory = {}, isOpen, onToggle, onRefetchDashboard }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // Backs the detail modal for both flows: the ssc/hsc edit icon ({ type, record }) and the
  // "Add New Record" flow (adds programLevelId/title on top, since that type isn't a fixed ssc/hsc key).
  const [recordModal, setRecordModal] = useState(null);

  const getSchoolRecord = (key) => academicHistory?.[key] ?? {};
  // Flattened view for display — the mutable/immutable split only matters for the edit form.
  const getFlatSchoolRecord = (key) => {
    const raw = getSchoolRecord(key);
    return { ...raw.immutable, ...raw.mutable };
  };

  const handleSaveRecord = async () => {
    setRecordModal(null);
    // Display always reflects the freshly refetched dashboard response — never a locally cached copy.
    await onRefetchDashboard?.();
  };

  const handleAddRecordContinue = ({ type, record, programLevelId, title }) => {
    setIsAddModalOpen(false);
    setRecordModal({ type, record, programLevelId, title });
  };

  // Same as UG/PG below: a record with none of these fields set means the student hasn't
  // filled it in yet, so its block shouldn't render at all.
  const hasSchoolData = (record) =>
    Boolean(record.boardName || record.streamName || record.schoolName || record.seatNo || record.passingYear || record.percentage);

  const schoolBlocks = ["ssc", "hsc"].flatMap((key) => {
    const record = getFlatSchoolRecord(key);
    if (!hasSchoolData(record)) return [];
    return [{
      title: SCHOOL_RECORD_TITLES[key],
      fields: [
        { label: "Board Name :", value: record.boardName },
        // Stream is only part of the HSC (12th) record
        ...(key === "hsc" ? [{ label: "Stream :", value: record.streamName }] : []),
        { label: "School Name :", value: record.schoolName },
        { label: "Seat Number :", value: record.seatNo },
        { label: "Passing Year :", value: record.passingYear },
        { label: "Percentage (%) :", value: record.percentage ? `${formatScore(record.percentage)}%` : null },
      ],
      editLabel: `Edit ${key === "ssc" ? "SSC" : "HSC"} Academic Record`,
      editPayload: { type: key, record: getSchoolRecord(key) },
    }];
  });

  const qualOrder = { PG: 1, UG: 2 };
  const collegeItems = ["ug", "pg", "diploma", "phd", "other"].flatMap((level) =>
    Array.isArray(academicHistory?.[level])
      ? academicHistory[level].filter(hasAnyValue).map((item) => ({ ...item, _level: level }))
      : []
  );
  const sortedCollege = [...collegeItems].sort((a, b) => {
    const wA = qualOrder[a._level.toUpperCase()] || 99;
    const wB = qualOrder[b._level.toUpperCase()] || 99;
    return wA - wB;
  });
  // Each item's actual values live under mutable/immutable (same split as the ssc/hsc records),
  // not as flat top-level properties.
  const collegeBlocks = sortedCollege.map((item) => {
    const flat = { ...(item.immutable ?? {}), ...(item.mutable ?? {}) };
    const levelLabel = LEVEL_LABELS[item._level] || item._level.toUpperCase();
    return {
      title: `${levelLabel} Information :`,
      fields: [
        { label: "University :", value: flat.universityName },
        { label: "Institute :", value: flat.instituteName },
        { label: "Enrollment No :", value: flat.seatNo },
        { label: "Obtained CGPA :", value: formatScore(flat.cgpa) },
        { label: "Passing Year :", value: flat.passingYear },
      ],
      editLabel: `Edit ${levelLabel} Academic Record`,
      editPayload: {
        type: item._level,
        record: { mutable: item.mutable, immutable: item.immutable },
        programLevelId: item.programLevelId ?? PROGRAM_LEVEL_ID[item._level],
        title: `${levelLabel} Academic Record`,
      },
    };
  });

  const allBlocks = [...schoolBlocks, ...collegeBlocks];

  const headerTemplate = (
    <div className="w-100 d-flex align-items-center justify-content-between gap-2">
      <div className="flex align-items-center gap-2">
        <span className="flex align-items-center">
          <Iconify icon="solar:document-text-bold" width={16} />
        </span>
        <span>Previous Academic Organization Records</span>
      </div>
      <Button
        className="mr-2"
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        leftIcon="material-symbols:add-rounded"
        onClick={(e) => {
          // The header row is itself the accordion's toggle link — stop the click
          // from bubbling into it (and from following its "#" href) before opening the modal.
          e.preventDefault();
          e.stopPropagation();
          setIsAddModalOpen(true);
        }}
      >
        Add New Record
      </Button>
    </div>
  );

  return (
    <>
      <Accordion activeIndex={isOpen ? 0 : null} onTabChange={onToggle}>
        <AccordionTab header={headerTemplate}>
          {allBlocks.length === 0 && (
            <p className="sp-info-value">No records found.</p>
          )}

          {allBlocks.map((block, bIdx) => (
            <div className="sp-history-block" key={bIdx}>
              <div className="sp-history-title-row">
                <h4 className="sp-history-title">{block.title}</h4>
                {block.editPayload && (
                  <Iconify
                    icon="material-symbols:edit-outline"
                    width={16}
                    className="sp-history-edit-icon"
                    targetId={`edit-${bIdx}`}
                    ttLabel={block.editLabel}
                    onClick={() => setRecordModal(block.editPayload)}
                  />
                )}
              </div>
              <div className="sp-info-grid">
                {block.fields.map((field, fIdx) => (
                  <InfoField key={fIdx} label={field.label} value={field.value} />
                ))}
              </div>
            </div>
          ))}
        </AccordionTab>
      </Accordion>

      <AddAcademicRecordModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onContinue={handleAddRecordContinue}
      />

      <EditAcademicRecordModal
        isOpen={Boolean(recordModal)}
        type={recordModal?.type}
        record={recordModal?.record}
        programLevelId={recordModal?.programLevelId}
        title={recordModal?.title}
        onClose={() => setRecordModal(null)}
        onSave={handleSaveRecord}
      />
    </>
  );
};

AcademicHistoryInfo.propTypes = {
  academicHistory: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRefetchDashboard: PropTypes.func,
};

export default AcademicHistoryInfo;
